// Which subtitle track is showing, and why.
//
// Both players carried their own copy of this: an effect that read the stored
// language and a handler that wrote it. Two copies of the same rules is how
// they drift, and the rules had a hole in them anyway -- turning subtitles OFF
// *removed* the key, which is the same state as never having chosen. So the
// next video could not tell "this watcher does not want subtitles" from "this
// watcher has no preference yet", and both meant off.
//
// Off is now a stored value like any other, which leaves room for the default
// this product actually wants: English on, because every video is recorded in
// it and the translated tracks are derived from it.

import { useCallback, useEffect, useRef, useState } from 'react';

export const SUBTITLE_KEY = 'preferredSubtitleLanguage';
export const SUBTITLES_OFF = 'off';
const DEFAULT_LANG = 'en';

// Pick the track to start with. A stored language wins whenever the video
// still carries it; a stored "off" wins outright.
export function chooseSubtitle(tracks, stored) {
  if (!Array.isArray(tracks) || tracks.length === 0) return null;
  if (stored === SUBTITLES_OFF) return null;
  if (stored) return tracks.find((track) => track.srclang === stored) || null;
  return tracks.find((track) => track.srclang === DEFAULT_LANG) || null;
}

function showOnly(player, subtitle) {
  const tracks = player?.textTracks?.();
  if (!tracks) return;
  for (let i = 0; i < tracks.length; i += 1) {
    const track = tracks[i];
    if (track.kind !== 'subtitles') continue;
    if (!subtitle) track.mode = 'disabled';
    else track.mode = track.language === subtitle.srclang ? 'showing' : 'hidden';
  }
}

export function useSubtitles({ playerRef, subtitles, isPlayerReady }) {
  const [selected, setSelected] = useState(null);
  const selectedRef = useRef(null);
  const decided = useRef(false);

  // Decide once per video. `subtitles` is a fresh array on every parent
  // render, so keying the decision off its identity would re-apply -- and
  // undo -- a choice made mid-playback. The track sources are the identity
  // that actually matters.
  const trackKey = (subtitles || []).map((track) => `${track.srclang}:${track.src}`).join('|');

  useEffect(() => {
    decided.current = false;
  }, [trackKey]);

  useEffect(() => {
    if (!isPlayerReady || decided.current || !subtitles?.length) return;
    decided.current = true;

    let stored = null;
    try {
      stored = localStorage.getItem(SUBTITLE_KEY);
    } catch {
      stored = null;
    }

    const choice = chooseSubtitle(subtitles, stored);
    selectedRef.current = choice;
    setSelected(choice);
  }, [isPlayerReady, subtitles]);

  // Apply the choice to the player, and again once the media loads: video.js
  // can reset a track's mode while it is still attaching the remote tracks,
  // so setting it a single time on ready is not reliably sticky.
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !isPlayerReady) return undefined;

    const apply = () => showOnly(player, selectedRef.current);
    apply();
    player.on('loadeddata', apply);
    return () => player.off('loadeddata', apply);
  }, [playerRef, isPlayerReady, selected]);

  const select = useCallback((subtitle) => {
    selectedRef.current = subtitle;
    setSelected(subtitle);
    try {
      localStorage.setItem(SUBTITLE_KEY, subtitle ? subtitle.srclang : SUBTITLES_OFF);
    } catch {
      // A browser with storage blocked still gets the change for this session.
    }
  }, []);

  return { selected, select };
}

export default useSubtitles;
